import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { FiMail, FiLock, FiUser, FiCheckCircle } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import config from "../config/config.json";
import ReCAPTCHA from "react-google-recaptcha";

const Register = () => {
  const site_key = process.env.REACT_APP_SITE_KEY;

  console.log(site_key);

  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm();

  const name = watch("name");
  const email = watch("email");
  const password = watch("password");

  const onSubmit = async ({ name, email, password }) => {
    if (!captchaToken) {
      setError("Please verify that you are not a robot.");
      setIsLoading(false);
      return;
    }

    setError("");
    setSuccess("");
    setIsLoading(true);

    try {
      const response = await axios.post(config.APP_URL + "/utahdata/register", {
        name,
        email,
        password,
        account_type: "unverified",
      });

      const { success, message } = response.data;

      if (response.status === 201) {
        setSuccess(
          message || "Registration successful. Account is under review."
        );
        reset();

        // Optional: Auto-hide success message after 10 seconds
        setTimeout(() => setSuccess(""), 10000);
      } else {
        setError(message || "Registration failed. Please try again.");
        reset();
        setTimeout(() => setError(""), 10000);
      }
    } catch (err) {
      console.error("Registration error:", err);
      setError(
        err?.response?.data?.message ||
          "Something went wrong. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Header />
      <section
        className="min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat py-12"
        style={{ backgroundImage: "url('/img/login.png')" }}
      >
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white/10 backdrop-blur-md border border-white/30 p-10 rounded-2xl shadow-lg w-full max-w-md"
        >
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
            Create a new account
          </h2>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Name */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-900 mb-1">
                Full Name
              </label>
              <FiUser className="absolute left-3 top-[52%] transform -translate-y-1/2 text-gray-400" />
              {name?.length >= 2 && !errors.name && (
                <FiCheckCircle className="absolute right-3 top-[52%] transform -translate-y-1/2 text-green-500" />
              )}
              <input
                type="text"
                {...register("name", {
                  required: "Name is required",
                  minLength: {
                    value: 2,
                    message: "Name must be at least 2 characters",
                  },
                })}
                placeholder="Your full name"
                className={`pl-10 pr-10 w-full py-3 border rounded-lg focus:outline-none transition ${
                  errors.name
                    ? "border-red-500"
                    : name?.length >= 2
                      ? "border-green-500"
                      : "border-gray-300 focus:border-blue-600 ring-1 ring-blue-100 focus:ring-blue-500"
                }`}
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.name.message}
                </p>
              )}
            </div>

            {/* Email */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-900 mb-1">
                Email Address
              </label>
              <FiMail className="absolute left-3 top-[52%] transform -translate-y-1/2 text-gray-400" />
              {email &&
                /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) &&
                !errors.email && (
                  <FiCheckCircle className="absolute right-3 top-[52%] transform -translate-y-1/2 text-green-500" />
                )}
              <input
                type="email"
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Invalid email format",
                  },
                })}
                placeholder="you@example.com"
                className={`pl-10 pr-10 w-full py-3 border rounded-lg focus:outline-none transition ${
                  errors.email
                    ? "border-red-500"
                    : email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
                      ? "border-green-500"
                      : "border-gray-300 focus:border-blue-600 ring-1 ring-blue-100 focus:ring-blue-500"
                }`}
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-900 mb-1">
                Password
              </label>
              <FiLock className="absolute left-3 top-[52%] transform -translate-y-1/2 text-gray-400" />
              {password?.length >= 5 && !errors.password && (
                <FiCheckCircle className="absolute right-3 top-[52%] transform -translate-y-1/2 text-green-500" />
              )}
              <input
                type="password"
                {...register("password", {
                  required: "Password is required",
                  minLength: {
                    value: 5,
                    message: "Password must be at least 5 characters",
                  },
                })}
                placeholder="Create a password"
                className={`pl-10 pr-10 w-full py-3 border rounded-lg focus:outline-none transition ${
                  errors.password
                    ? "border-red-500"
                    : password?.length >= 5
                      ? "border-green-500"
                      : "border-gray-300 focus:border-blue-600 ring-1 ring-blue-100 focus:ring-blue-500"
                }`}
              />
              {errors.password && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.password.message}
                </p>
              )}
            </div>

            <ReCAPTCHA
              sitekey={site_key}
              onChange={handleCaptchaChange}
              theme="light"
              className="flex justify-center w-full"
              required
            />

            {success && (
              <p className="text-green-600 bg-black/60 px-3 py-2 rounded-md text-sm text-center font-semibold shadow-md">
                {success}
              </p>
            )}

            {error && (
              <p className="text-red-600 bg-black/60 px-3 py-2 rounded-md text-sm text-center font-semibold shadow-md">
                {error}
              </p>
            )}

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
              disabled={!captchaToken}
            >
              {isLoading ? "Registering..." : "Register"}
            </motion.button>
            <p className="text-center font-bold">
              Already have an account?{" "}
              <Link to="/login" className="font-bold">
                Sign In
              </Link>
            </p>
          </form>
        </motion.div>
      </section>
      <Footer />
    </>
  );
};

export default Register;
